import {pointerUtils} from '@wix/document-manager-core'
import {ReportableError} from '@wix/document-manager-utils'
import type {
    CompRef,
    Pointer,
    PossibleViewModes,
    PS,
    VariableConnections,
    VariableData,
    VariantPointer
} from '@wix/document-services-types'
import _ from 'lodash'
import constants from '../constants/constants'
import dataIds from '../dataModel/dataIds'
import dataModel from '../dataModel/dataModel'
import dataSerialization from '../dataModel/dataSerialization'
import hooks from '../hooks/hooks'
import dsUtils from '../utils/utils'
import variants from '../variants/variants'
import variantsUtils from '../variants/variantsUtils'
import variableUtils from './variablesUtils'
import documentModeInfo from '../documentMode/documentModeInfo'

const {getPointer} = pointerUtils
const {DATA_TYPES, NAMESPACES_SUPPORTING_VARIABLES} = constants
const VARIABLES_NAMESPACE = DATA_TYPES.variables

const createVariablesError = (message: string) => {
    return new ReportableError({
        message,
        errorType: 'variablesError'
    })
}

const validateCompPointer = (ps: PS, componentPointer: Pointer) => {
    if (!componentPointer || !ps.dal.isExist(componentPointer)) {
        throw createVariablesError('The component pointer is not passed or it does not exist')
    }
}
const validateCompAndVariablePointers = (ps: PS, componentPointer: Pointer, variablePointer: Pointer) => {
    validateCompPointer(ps, componentPointer)
    if (!variablePointer || !ps.dal.isExist(variablePointer)) {
        throw createVariablesError('The variable pointer is not passed or it does not exist')
    }
    const currentVariableIds = variableUtils.getComponentVariablesIds(ps, componentPointer)
    if (!_.includes(currentVariableIds, variablePointer.id)) {
        throw createVariablesError(`The component ${componentPointer.id} didn't define ${variablePointer.id} variable`)
    }
}
const validateVariants = (ps: PS, compPointerWithVariants: VariantPointer) => {
    _.forEach(compPointerWithVariants.variants, variant => {
        if (!ps.dal.isExist(variant)) {
            throw createVariablesError(`Passing component with non-existing variant ${variant.id}`)
        }
    })
}

const isVariableInUse = (ps: PS, variablePointer: Pointer) => {
    const references = ps.extensionAPI.relationships.getReferencesToPointer(variablePointer)
    return references.some(({type}) => type !== VARIABLES_NAMESPACE)
}

const validateVariableNotInUse = (ps: PS, variablePointer: Pointer) => {
    if (isVariableInUse(ps, variablePointer)) {
        throw createVariablesError(`The variable is in use`)
    }
}

const getVariableRef = (id: string = dataIds.generateNewId(VARIABLES_NAMESPACE)): Pointer => ({
    id,
    type: VARIABLES_NAMESPACE
})
const getVariableToAddRef = (): Pointer => getVariableRef()

const getGlobalVariableToAddRef = (ps: PS, variableData: VariableData, customVariableId?: string): Pointer =>
    getVariableRef(customVariableId)

const getMasterPageRef = (ps: PS): CompRef => ps.pointers.components.getMasterPage(documentModeInfo.getViewMode(ps))

const getComponentVariablesList = (ps: PS, componentPointer: Pointer): Pointer[] => {
    validateCompPointer(ps, componentPointer)
    return variableUtils
        .getComponentVariablesIds(ps, componentPointer)
        .map((variableId: string) => ps.pointers.getPointer(variableId, VARIABLES_NAMESPACE))
}

const getVariable = (ps: PS, componentPointer: CompRef, variablePointer: Pointer) => {
    validateCompAndVariablePointers(ps, componentPointer, variablePointer)
    const variableDataItem = ps.dal.get(variablePointer)
    const variableDefaultValueDataItem = variantsUtils.getDataConsideringVariants(
        ps,
        variablePointer,
        'value',
        VARIABLES_NAMESPACE
    )
    const variableValueType = ps.extensionAPI.variables.getVariableValueType(variableDataItem.type)
    return {
        name: variableDataItem.name,
        type: variableDataItem.type,
        value: {type: variableValueType, value: variableDefaultValueDataItem.value}
    }
}

const getGlobalVariable = (ps: PS, variablePointer: Pointer): VariableData =>
    getVariable(ps, getMasterPageRef(ps), variablePointer)

const addVariable = (
    ps: PS,
    variableToAddRef: VariantPointer,
    componentPointer: Pointer,
    variableData: VariableData
) => {
    validateCompPointer(ps, componentPointer)
    if (ps.dal.isExist(variableToAddRef)) {
        throw createVariablesError('Variable with the same id already exists')
    }
    if (!variableData) {
        throw createVariablesError('The variableData is missing')
    }
    if (!variableData.type) {
        throw createVariablesError('The variableData is missing variable type')
    }
    if (!variableData.value) {
        throw createVariablesError('The variableData is missing default value for the variant')
    }

    const pagePointer = ps.pointers.components.getPageOfComponent(componentPointer)
    const pageId = pagePointer?.id

    //create variable
    const variableID = dataSerialization.addSerializedItemToPage(
        ps,
        pageId,
        {
            name: variableData.name,
            type: variableData.type
        },
        variableToAddRef.id,
        VARIABLES_NAMESPACE
    )
    //add default value
    variantsUtils.updateDataConsideringVariants(ps, variableToAddRef, 'value', variableData.value, VARIABLES_NAMESPACE)

    const variablesListPointer = dataModel.getComponentDataPointerByType(ps, componentPointer, VARIABLES_NAMESPACE)
    const doesVariablesListExists = ps.dal.isExist(variablesListPointer)
    if (!doesVariablesListExists) {
        //create variables list and link comp to it
        const variablesListID = dataModel.addDeserializedItemToPage(ps, pageId, VARIABLES_NAMESPACE, {
            type: 'VariablesList',
            variables: [`#${variableID}`]
        })
        dataModel.linkComponentToItemByType(ps, componentPointer, variablesListID, VARIABLES_NAMESPACE)
    } else {
        //add variable to existing variables list of the component
        const variablesListDataItem = ps.dal.get(variablesListPointer)
        ps.dal.set(variablesListPointer, {
            ...variablesListDataItem,
            variables: [...variablesListDataItem.variables, `#${variableID}`]
        })
    }
}

const addGlobalVariable = (ps: PS, variableToAddRef: VariantPointer, variableData: VariableData): void =>
    addVariable(ps, variableToAddRef, getMasterPageRef(ps), variableData)

const addVariableWithCustomId = (ps: PS, variableId: string, componentPointer: Pointer, variableData) => {
    return addVariable(
        ps,
        {
            id: variableId,
            type: VARIABLES_NAMESPACE
        },
        componentPointer,
        variableData
    )
}

const removeVariable = (ps: PS, componentPointer: Pointer, variablePointer: Pointer) => {
    validateCompAndVariablePointers(ps, componentPointer, variablePointer)
    validateVariableNotInUse(ps, variablePointer)

    //remove all the values of the variable
    variantsUtils.removeDataConsideringVariants(ps, variablePointer, 'value', VARIABLES_NAMESPACE)

    //remove variable from variables list
    const variablesListPointer = dataModel.getComponentDataPointerByType(ps, componentPointer, VARIABLES_NAMESPACE)
    const variablesListDataItem = ps.dal.get(variablesListPointer)
    ps.dal.set(variablesListPointer, {
        ...variablesListDataItem,
        variables: _.without(variablesListDataItem.variables, `#${variablePointer.id}`)
    })

    //remove the variable
    ps.dal.remove(variablePointer)
}

const removeGlobalVariable = (ps: PS, variablePointer: Pointer): void =>
    removeVariable(ps, getMasterPageRef(ps), variablePointer)

const updateVariableDefinition = (
    ps: PS,
    componentPointer: Pointer,
    variablePointer: Pointer,
    variableDefinition: Partial<VariableData>
) => {
    validateCompAndVariablePointers(ps, componentPointer, variablePointer)
    const currentVariable = ps.dal.get(variablePointer)
    if (variableDefinition.type && currentVariable.type !== variableDefinition.type) {
        throw createVariablesError('Variable type cannot be changed')
    }

    if (variableDefinition.name) {
        ps.dal.set(variablePointer, {...currentVariable, name: variableDefinition.name})
    }

    if (variableDefinition.value) {
        variantsUtils.updateDataConsideringVariants(
            ps,
            variablePointer,
            'value',
            variableDefinition.value,
            VARIABLES_NAMESPACE
        )
    }
}

const updateGlobalVariableDefinition = (
    ps: PS,
    variablePointer: Pointer,
    variableDefinition: Partial<VariableData>
): void => updateVariableDefinition(ps, getMasterPageRef(ps), variablePointer, variableDefinition)

const getVariableData = (ps: PS, compPointerWithVariants: CompRef, variablePointer: Pointer) => {
    validateCompPointer(ps, compPointerWithVariants)
    const templatePointer = pointerUtils.getRepeatedItemPointerIfNeeded(compPointerWithVariants)
    return ps.extensionAPI.variables.getVariableData(templatePointer, variablePointer)
}

const getGlobalVariableData = (ps: PS, variablePointer: Pointer) => {
    return getVariableData(ps, getMasterPageRef(ps), variablePointer)
}

const removeVariableData = (ps: PS, compPointerWithVariants: VariantPointer, variablePointer: Pointer) => {
    validateCompAndVariablePointers(ps, compPointerWithVariants, variablePointer)
    if (_.isEmpty(compPointerWithVariants.variants)) {
        throw createVariablesError(
            'Cannot remove the default variable value, please use the components.variables.remove function for that'
        )
    }
    validateVariants(ps, compPointerWithVariants)
    const variablePointerWithVariants = variants.getPointerWithVariants(
        ps,
        variablePointer,
        compPointerWithVariants.variants
    )
    variantsUtils.removeDataConsideringVariants(ps, variablePointerWithVariants, 'value', VARIABLES_NAMESPACE)
}

const removeGlobalVariableData = (ps: PS, variablePointer: Pointer) => {
    return removeVariableData(ps, getMasterPageRef(ps), variablePointer)
}

const updateVariableData = (
    ps: PS,
    compPointerWithVariants: VariantPointer,
    variablePointer: Pointer,
    variableData
) => {
    validateCompAndVariablePointers(ps, compPointerWithVariants, variablePointer)
    validateVariants(ps, compPointerWithVariants)
    const variablePointerWithVariants = compPointerWithVariants.variants
        ? variants.getPointerWithVariants(ps, variablePointer, compPointerWithVariants.variants)
        : variablePointer
    variantsUtils.updateDataConsideringVariants(
        ps,
        variablePointerWithVariants,
        'value',
        variableData,
        VARIABLES_NAMESPACE
    )
}

const updateGlobalVariableData = (ps: PS, variablePointer: Pointer, variableData) => {
    return updateVariableData(ps, getMasterPageRef(ps), variablePointer, variableData)
}

const getComponentsUsingVariable = (
    ps: PS,
    variablePointer: Pointer,
    viewMode: PossibleViewModes = constants.VIEW_MODES.DESKTOP
) => ps.extensionAPI.variables.getComponentsUsingVariable(variablePointer, viewMode)

function removeOutOfScopeVariables(ps: PS, compPointer: Pointer) {
    const ancestorsVariableIds = variableUtils.collectAncestorsVariablesIds(ps, compPointer)
    removeOutOfScopeVariablesRecursively(ps, compPointer, ancestorsVariableIds)
}

function removeOutOfScopeVariablesRecursively(ps: PS, compPointer: Pointer, scopedVariableIds: string[]) {
    NAMESPACES_SUPPORTING_VARIABLES.forEach(namespace =>
        variableUtils.removeOutOfScopeVariablesFromRefArray(ps, compPointer, namespace, scopedVariableIds)
    )
    const componentChildren = ps.pointers.full.components.getChildren(compPointer)
    scopedVariableIds.push(variableUtils.getComponentVariablesIds(ps, compPointer))
    _.forEach(componentChildren, child => removeOutOfScopeVariablesRecursively(ps, child, scopedVariableIds))
}

const getVariableConnections = (ps: PS, obj) => {
    const {variableConnections} = obj ?? {}
    if (!variableConnections) return {}

    return _.mapValues(variableConnections, variableConnection => {
        if (!_.isString(variableConnection)) {
            throw createVariablesError('Invalid variable connections')
        }

        return {
            id: dsUtils.stripHashIfExists(variableConnection),
            type: 'variables'
        }
    })
}

const getComponentVariableConnections = (ps: PS, componentPointer: CompRef): VariableConnections => {
    const variableConnections = {}
    for (const namespace of NAMESPACES_SUPPORTING_VARIABLES) {
        const dataItemPointer = variantsUtils.getComponentDataPointerConsideringVariants(
            ps,
            componentPointer,
            namespace
        )
        const dataItem = dataItemPointer && ps.dal.get(dataItemPointer)
        const connections = dataItem?.variableConnections
        if (connections) {
            variableConnections[namespace] = {}
            for (const {path, variableId} of connections) {
                variableConnections[namespace][path] = getPointer(variableId, VARIABLES_NAMESPACE)
            }
        }
    }

    return variableConnections
}

const connect = (ps: PS, obj, property: string, variablePointer: Pointer) => {
    if (_.isNil(variablePointer) || !ps.dal.isExist(variablePointer)) {
        throw createVariablesError('The variable pointer is not passed or it does not exist')
    }

    if (!_.has(obj, property)) {
        throw createVariablesError('Property does not exist on object')
    }

    if (_.isArray(obj)) {
        return _.set(obj, [property, 'variableConnections', ''], variablePointer.id)
    }

    return _.set(obj, ['variableConnections', property], variablePointer.id)
}

const disconnect = (ps: PS, obj, property: string) => {
    if (_.isArray(obj)) {
        if (_.has(obj, [property, 'variableConnections'])) delete obj[property].variableConnections['']
        return obj
    }

    if (_.has(obj, ['variableConnections', property])) delete obj.variableConnections[property]
    return obj
}
hooks.registerHook(hooks.HOOKS.CHANGE_PARENT.AFTER, removeOutOfScopeVariables)

export default {
    getVariableToAddRef,
    getGlobalVariableToAddRef,
    getComponentVariablesList,
    getVariable,
    getGlobalVariable,
    addVariable,
    addGlobalVariable,
    addVariableWithCustomId,
    removeVariable,
    removeGlobalVariable,
    updateVariableDefinition,
    updateGlobalVariableDefinition,
    getVariableData,
    getGlobalVariableData,
    removeVariableData,
    removeGlobalVariableData,
    updateVariableData,
    updateGlobalVariableData,
    getComponentsUsingVariable,
    getVariableConnections,
    getComponentVariableConnections,
    connect,
    disconnect
}
